import { useParams, Link, useSearchParams } from "react-router-dom";
import { CheckCircle, ShoppingBag, Loader2, AlertCircle, Tag } from "lucide-react";
import { useEffect, useState } from "react";
import Ticker from "@/components/Ticker";
import Navbar from "@/components/Navbar";
import Footer from "@/components/Footer";
import { Button } from "@/components/ui/button";
import { supabase } from "@/integrations/supabase/client";
import { useCart } from "@/contexts/CartContext";

interface DiscountInfo {
  code: string;
  amount: number;
}

const OrderConfirmation = () => {
  const { orderId } = useParams<{ orderId: string }>();
  const [searchParams] = useSearchParams();
  const sessionId = searchParams.get("session_id");
  const [verifying, setVerifying] = useState(!!sessionId);
  const [verified, setVerified] = useState(!sessionId);
  const [error, setError] = useState(false);
  const [discount, setDiscount] = useState<DiscountInfo | null>(null);
  const { clearCart } = useCart();

  useEffect(() => {
    try {
      const saved = localStorage.getItem("plym_order_discount");
      if (saved) {
        setDiscount(JSON.parse(saved));
        localStorage.removeItem("plym_order_discount");
      }
    } catch {}
  }, []);

  useEffect(() => {
    if (!sessionId || !orderId) return;

    const verify = async () => {
      try {
        const { data, error: fnError } = await supabase.functions.invoke("verify-payment", {
          body: { sessionId, orderId },
        });

        if (fnError || data?.error) {
          setError(true);
        } else {
          setVerified(true);
          clearCart();
        }
      } catch {
        setError(true);
      } finally {
        setVerifying(false);
      }
    };

    verify();
  }, [sessionId, orderId]);

  return (
    <div className="min-h-screen flex flex-col">
      <Ticker />
      <Navbar />

      <div className="flex-1 flex items-center justify-center py-20">
        <div className="text-center max-w-md mx-auto px-4">
          {verifying ? (
            <>
              <Loader2 className="w-16 h-16 text-primary mx-auto mb-6 animate-spin" />
              <h1 className="font-display text-2xl md:text-3xl font-bold mb-3">
                Confirming Payment…
              </h1>
              <p className="text-muted-foreground font-body">
                Please wait while we verify your payment.
              </p>
            </>
          ) : error ? (
            <>
              <AlertCircle className="w-16 h-16 text-destructive mx-auto mb-6" />
              <h1 className="font-display text-2xl md:text-3xl font-bold mb-3">
                Payment Issue
              </h1>
              <p className="text-muted-foreground font-body mb-8">
                We couldn't verify your payment. If you were charged, please contact us and we'll sort it out.
              </p>
              <Link to="/shop">
                <Button>Back to Shop</Button>
              </Link>
            </>
          ) : (
            <>
              <CheckCircle className="w-16 h-16 text-green-500 mx-auto mb-6" />
              <h1 className="font-display text-2xl md:text-3xl font-bold mb-3">
                Order Confirmed!
              </h1>
              <p className="text-muted-foreground font-body mb-2">
                Thank you for your order. We'll send you a confirmation email shortly.
              </p>
              {orderId && (
                <p className="text-sm text-muted-foreground font-body mb-4">
                  Order reference:{" "}
                  <span className="font-mono font-medium text-foreground">
                    #{orderId.slice(0, 8)}
                  </span>
                </p>
              )}

              {discount && (
                <div className="bg-green-50 dark:bg-green-950/30 border border-green-200 dark:border-green-800 rounded-lg p-3 mb-6 inline-flex items-center gap-2 text-sm font-body">
                  <Tag className="w-4 h-4 text-green-600 dark:text-green-400" />
                  <span className="text-green-700 dark:text-green-300">
                    Code <span className="font-bold">{discount.code}</span> saved you{" "}
                    <span className="font-bold">£{discount.amount.toFixed(2)}</span>
                  </span>
                </div>
              )}

              <div className="flex gap-3 justify-center">
                <Link to="/shop">
                  <Button variant="outline" className="gap-2">
                    <ShoppingBag className="w-4 h-4" />
                    Continue Shopping
                  </Button>
                </Link>
                <Link to="/">
                  <Button>Back to Home</Button>
                </Link>
              </div>
            </>
          )}
        </div>
      </div>

      <Footer />
    </div>
  );
};

export default OrderConfirmation;
